import React, { useState, useEffect } from "react";
import {
  FiLogOut,
  FiEdit2,
  FiLock,
  FiMail,
  FiPhone,
  FiCheckCircle,
  FiCalendar,
  FiUser,
} from "react-icons/fi";
import userPic from "../assets/user.png";
import useAuth from "../useAuth";
import EditProfileModal from "../Components/EditProfileModal";
import { useNavigate } from "react-router-dom";
import ChangePasswordModal from "../Components/ChangePasswordModal";

export const ProfilePage = () => {
  const { user, logout, changePassword } = useAuth();
  const navigate = useNavigate();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    const pwd = user.passwordChangedAt;
    // open change-password when passwordChangedAt is null/undefined or equals the zero-value sentinel
    if (pwd === "0001-01-01T00:00:00") {
      setIsPasswordModalOpen(true);
    }
  }, [user]);

  // Mock handlers for now
  const handleEditProfile = (updatedData) => {
    console.log("Updated Profile Data:", updatedData);
    // Here you would typically call an API to update the user profile
    setIsEditModalOpen(false);
  };

  const handleChangePassword = (passwordData) => {
    (async () => {
      try {
        await changePassword({
          username: user?.username,
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword,
        });
      } catch (e) {
        // error handled in changePassword
      } finally {
        setIsPasswordModalOpen(false);
      }
    })();
  };

  const handleLogout = () => {
    let userRes = window.confirm("Are you sure, You want to logout?");

    if (userRes) {
      logout();
    } else return null;
  };

  return (
    <div className="min-h-screen bg-gray-50/50 py-12 px-4 sm:px-6 lg:px-8 mt-10">
      <div className="max-w-4xl mx-auto">
        {/* Profile Header Card */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden mb-6 transition-all hover:shadow-2xl">
          <div className="h-32 bg-gradient-to-r from-blue-600 to-indigo-700 relative">
            <div className="absolute -bottom-12 left-8">
              <div className="relative">
                <img
                  src={user?.profilePhoto || userPic}
                  alt="Profile"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = userPic;
                  }}
                  className="w-24 h-24 rounded-full border-4 border-white shadow-lg object-cover bg-white"
                />
                <div
                  className="absolute bottom-1 right-1 w-5 h-5 bg-green-500 border-2 border-white rounded-full"
                  title="Online"
                ></div>
              </div>
            </div>
          </div>

          <div className="pt-16 pb-8 px-8">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">
                  {user?.userType === "admin" ? (user?.username || "Admin User") : (user?.ddcName || user?.username || "User Name")}
                </h1>
                <div className="flex flex-wrap gap-2 mt-2">
                  {user?.userType !== "admin" && (
                    <span className="bg-blue-50 px-3 py-1 rounded-full text-xs font-bold text-blue-600 border border-blue-100 uppercase tracking-wider">
                      {user?.districtName || "Jharkhand"}
                    </span>
                  )}
                  <span className="bg-indigo-50 px-3 py-1 rounded-full text-xs font-bold text-indigo-600 border border-indigo-100 uppercase tracking-wider">
                    {user?.userType || "User Type"}
                  </span>
                  <span className="bg-slate-50 px-3 py-1 rounded-full text-xs font-bold text-slate-600 border border-slate-100 uppercase tracking-wider">
                    {user?.designation || "Designation"}
                  </span>
                </div>
              </div>

              <div className="flex gap-3 w-full sm:w-auto">
                <button
                  className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 hover:border-gray-300 transition-all shadow-sm font-medium"
                  onClick={() => navigate("/my/profile/edit")}
                >
                  <FiEdit2 size={16} />
                  Edit Profile
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Conditional Content based on userType */}
          <div className="lg:col-span-2 space-y-6">
            {user?.userType === "admin" ? (
              /* Admin Simplified View */
              <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
                <h2 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                  <span className="p-2 bg-indigo-100 text-indigo-600 rounded-lg">
                    <FiLock size={20} />
                  </span>
                  System Administrator Account
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-1">
                    <label className="text-xs font-bold text-gray-400 uppercase tracking-wider">Username</label>
                    <p className="text-gray-900 font-semibold">{user?.username}</p>
                  </div>

                  <div className="space-y-1">
                    <label className="text-xs font-bold text-gray-400 uppercase tracking-wider">System Role</label>
                    <p className="text-gray-900 font-semibold uppercase">{user?.role}</p>
                  </div>

                  <div className="space-y-1">
                    <label className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2">
                      <FiCheckCircle size={12} /> Status
                    </label>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-green-100 text-green-800 uppercase">
                      Authorized
                    </span>
                  </div>

                  <div className="space-y-1">
                    <label className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2">
                      <FiCalendar size={12} /> Last Security Update
                    </label>
                    <p className="text-gray-900 font-semibold">
                      {user?.passwordChangedAt
                        ? new Date(user.passwordChangedAt).toLocaleDateString("en-IN", {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })
                        : "---"}
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              /* DDC Detailed View (Existing) */
              <>
                <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
                  <h2 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                    <span className="p-2 bg-blue-100 text-blue-600 rounded-lg">
                      <FiUser size={20} />
                    </span>
                    DDC Information
                  </h2>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-1">
                      <label className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2">
                        <FiMail size={12} /> Email Address
                      </label>
                      <p className="text-gray-900 font-semibold break-all">
                        {user?.ddcEmailId || user?.email || "---"}
                      </p>
                    </div>

                    <div className="space-y-1">
                      <label className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2">
                        <FiPhone size={12} /> Mobile Number
                      </label>
                      <p className="text-gray-900 font-semibold">
                        {user?.ddcMobileNo || user?.mobile || "---"}
                      </p>
                    </div>

                    <div className="space-y-1">
                      <label className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2">
                        <FiCheckCircle size={12} /> Account Status
                      </label>
                      <div>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold uppercase ${user?.disabled ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
                          {user?.disabled ? 'Disabled' : 'Active'}
                        </span>
                      </div>
                    </div>

                    <div className="space-y-1">
                      <label className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2">
                        <FiCalendar size={12} /> Password Updated
                      </label>
                      <p className="text-gray-900 font-semibold">
                        {user?.passwordChangedAt
                          ? new Date(user.passwordChangedAt).toLocaleDateString("en-IN", {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          })
                          : "---"}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
                  <h2 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                    <span className="p-2 bg-emerald-100 text-emerald-600 rounded-lg">
                      <FiCheckCircle size={20} />
                    </span>
                    Nodal Officer Details
                  </h2>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-1">
                      <label className="text-xs font-bold text-gray-400 uppercase tracking-wider">Name</label>
                      <p className="text-gray-900 font-semibold">
                        {user?.nodalOfficerDetails?.name || "---"}
                      </p>
                    </div>

                    <div className="space-y-1">
                      <label className="text-xs font-bold text-gray-400 uppercase tracking-wider">Designation</label>
                      <p className="text-gray-900 font-semibold">
                        {user?.nodalOfficerDetails?.designation || "---"}
                      </p>
                    </div>

                    <div className="space-y-1">
                      <label className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2">
                        <FiPhone size={12} /> Mobile
                      </label>
                      <p className="text-gray-900 font-semibold">
                        {user?.nodalOfficerDetails?.mobileNo || "---"}
                      </p>
                    </div>

                    <div className="space-y-1">
                      <label className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2">
                        <FiMail size={12} /> Email
                      </label>
                      <p className="text-gray-900 font-semibold break-all">
                        {user?.nodalOfficerDetails?.emailId || "---"}
                      </p>
                    </div>

                    <div className="space-y-1 md:col-span-2">
                      <label className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2">
                        <FiCalendar size={12} /> Date of Appointment
                      </label>
                      <p className="text-gray-900 font-semibold">
                        {user?.nodalOfficerDetails?.dateOfAppointment
                          ? new Date(user.nodalOfficerDetails.dateOfAppointment).toLocaleDateString("en-IN", {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          })
                          : "---"}
                      </p>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Quick Actions */}
          <div className="space-y-6">
            <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
              <h2 className="text-lg font-bold text-gray-800 mb-4 uppercase tracking-tight">
                Quick Actions
              </h2>
              <div className="space-y-3">

                <button
                  onClick={handleLogout}
                  className="w-full flex items-center justify-between p-3 text-left text-red-600 bg-red-50 hover:bg-red-100 rounded-xl transition-colors group"
                >
                  <span className="font-bold text-sm">Sign Out</span>
                  <FiLogOut className="transform group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <EditProfileModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        user={user}
        onSave={handleEditProfile}
      />
      <ChangePasswordModal
        isOpen={isPasswordModalOpen}
        onClose={() => setIsPasswordModalOpen(false)}
        onSave={handleChangePassword}
      />
    </div>
  );
};
